import { ClientModel } from 'api/models';
import { Avatar } from 'library/avatar';
import { DocumentTitle } from 'library/head';
import { CircularProgress } from 'library/loaders';
import { GridwallGrid } from 'modules/plugins/features/gridwall-access';
import VerifyGridwallAccessCodePage from 'modules/plugins/features/gridwall-access/components/VerifyGridwallAccessCode/VerifyGridwallAccessCode';
import { useGridWall } from 'modules/plugins/features/gridwall-access/hooks/useGridwall';
import { IllustrationView, NoImagesIllustration } from 'modules/shared/features/illustration';
import { Link, useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

const GridWallPage = () => {
    const { token } = useParams<{ token: string }>();
    const { gridWall, isLoading, error, needsAccessCode, isVerifyingAccess, submitAccessCode } = useGridWall();

    if (isLoading) {
        return (
            <div className="flex h-screen items-center justify-center bg-black">
                <CircularProgress size={48} className="text-white" />
            </div>
        );
    }

    if (error && !needsAccessCode) {
        return (
            <IllustrationView
                className="justify-center h-screen"
                illustration={<NoImagesIllustration />}
                title="Failed to load GridWall"
                message={
                    <>
                        We failed to load GridWall. Please try again.
                        <div className="mt-2">
                            <Link to="/" replace className="text-primary font-medium cursor-pointer">
                                Back to home
                            </Link>
                        </div>
                    </>
                }
            />
        );
    }

    // Show access code verification page
    if (needsAccessCode && token) {
        return <VerifyGridwallAccessCodePage token={token} isLoading={isVerifyingAccess} onSubmit={submitAccessCode} />;
    }

    // Show GridWall
    if (gridWall) {
        return (
            <>
                <DocumentTitle>{gridWall.name}</DocumentTitle>
                <div className="h-screen bg-black flex flex-col overflow-hidden">
                    <div className="flex-1 overflow-hidden">
                        <GridwallGrid grid={gridWall} isLoading={isLoading} />
                    </div>

                    <div className="flex-shrink-0 w-full p-2 px-3 flex items-center justify-between bg-black z-10 border-t border-gray-800">
                        <div className="flex-shrink-0">
                            {(gridWall?.client as ClientModel)?.logoUrl ? (
                                <img
                                    className="h-10"
                                    src={(gridWall?.client as ClientModel)?.logoUrl}
                                    alt={(gridWall?.client as ClientModel)?.name}
                                />
                            ) : (
                                <Avatar
                                    src={(gridWall?.client as ClientModel)?.logoUrl}
                                    alt={(gridWall?.client as ClientModel)?.name}
                                    backgroundColor={(gridWall?.client as ClientModel)?.preferences.primaryColor}
                                    size={40}
                                />
                            )}
                        </div>

                        <div
                            className={twMerge(
                                'text-white text-center max-w-[450px]',
                                gridWall?.footerMessage.length > 50 ? 'marquee' : ''
                            )}
                        >
                            <div className="whitespace-nowrap">{gridWall?.footerMessage} </div>
                        </div>

                        <div className="flex items-center gap-4 flex-shrink-0">
                            <Link to="/dashboard">
                                <div className="text-white text-xs hover:text-blue-400 transition-colors duration-200 underline underline-offset-2 cursor-pointer">
                                    Go to Dashboard
                                </div>
                            </Link>
                            <img className="h-4" src="/logos/gridwall-icon.svg" alt="GridWall logo" />
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Fallback
    return (
        <IllustrationView
            className="justify-center h-screen"
            illustration={<NoImagesIllustration />}
            title="Failed to load GridWall"
            message="Unable to load GridWall content."
        />
    );
};

export default GridWallPage;
